import { LoadMeshMessage, MeshLoadedMessage } from "../../../messages/rendering/RendererMessages";
import { Color, EdgesGeometry, LineBasicMaterial, LineSegments, Mesh, Object3D, SkinnedMesh } from "three";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { clockService } from "@modules/xr/ClockService";
import { MaterialQualitySystem } from "../material/MaterialQualitySystem";
import { meshLoadingTracker } from "./MeshLoadingTracker";
import { AnimationPlayback, MeshPieceAnimation, animationRegistry } from "@modules/animation";
import { modelPool } from "@modules/model/ModelPool";
export class MeshInstance {
    constructor(renderer, onMeshLoadedCallback, onMeshStartLoadingCallback, shadowOwnerModelUID) {
        this.renderer = renderer;
        this.onMeshLoadedCallback = onMeshLoadedCallback;
        this.onMeshStartLoadingCallback = onMeshStartLoadingCallback;
        this.shadowOwnerModelUID = shadowOwnerModelUID;
        this.isVisible = true;
        this.meshName = "";
        this.castShadow = false;
        this.receiveShadow = false;
        /** A scalar Scale being applied to the mesh after loading is complete. Used when importing cad-data that is scaled to millimeters or centimeters. */
        this.scale = 1;
        this.offsetGroup = new Object3D();
        this.applyOutline = false;
        this.outlineColor = "";
        this.cloneMaterials = false;
        this.updateAnimation = (delta = clockService.getDeltaToLastFrameInSeconds()) => {
            var _a;
            if (this.pieceAnimation) {
                this.pieceAnimation.update(delta);
            }
            else {
                (_a = this.animationPlayback) === null || _a === void 0 ? void 0 : _a.update(delta);
            }
            if (this.castShadow && this.isVisible) {
                this.triggerShadowUpdate();
            }
        };
        this.offsetGroup.matrixAutoUpdate = false;
    }
    get isLoaded() {
        return !!this.group;
    }
    destroy() {
        this.offsetGroup.parent && this.offsetGroup.parent.remove(this.offsetGroup);
        this.removeMesh();
        this.meshName = "";
        this.subMeshVisibilities = undefined;
    }
    removeMesh() {
        var _a, _b;
        if (this.group === undefined)
            return;
        if (this.animationOwnerUID !== undefined) {
            animationRegistry.unregister(this.animationOwnerUID, this);
            this.animationOwnerUID = undefined;
        }
        if (this.animationPlayback || this.pieceAnimation) {
            animationFrame$.onPreRender$.remove(this.updateAnimation);
            (_a = this.animationPlayback) === null || _a === void 0 ? void 0 : _a.dispose();
            this.animationPlayback = undefined;
            (_b = this.pieceAnimation) === null || _b === void 0 ? void 0 : _b.dispose();
            this.pieceAnimation = undefined;
        }
        this.group.parent && this.group.parent.remove(this.group);
        this.group = undefined;
        if (this.castShadow) {
            this.triggerShadowUpdate();
        }
    }
    setCastShadow(value) {
        this.castShadow = value;
        if (this.group) {
            this.group.traverse((child) => {
                child.castShadow = this.castShadow;
            });
            if (this.castShadow) {
                this.triggerShadowUpdate();
            }
        }
    }
    getCastShadow() {
        return this.castShadow;
    }
    setReceiveShadow(value) {
        this.receiveShadow = value;
        if (this.group) {
            this.group.traverse((child) => {
                child.receiveShadow = this.receiveShadow;
            });
        }
    }
    setVisible(visible) {
        let changed = (this.isVisible = visible);
        this.offsetGroup.visible = visible;
        if (this.group) {
            changed = this.group.visible !== visible;
            this.group.visible = visible;
            if (changed && this.castShadow) {
                this.triggerShadowUpdate();
            }
        }
        return changed;
    }
    getVisible() {
        return this.isVisible;
    }
    getGeometry() {
        if (!this.group)
            return;
        if (this.group instanceof Mesh) {
            return {
                geometry: this.group.geometry,
                mesh: this.group
            };
        }
        // Breadth-first search
        const stack = [...this.group.children];
        for (const child of stack) {
            if (child instanceof Mesh) {
                return {
                    geometry: child.geometry,
                    mesh: child
                };
            }
            stack.push(...child.children);
        }
        return undefined;
    }
    /**
     * @param meshName
     * @param addBarycentricCoordinates Used to calculate fast wireframe-shaders.
     * @param pieceName If the "mesh" is part of a bigger thing.
     * @param scale A scalar Scale being applied to the mesh after loading is complete. Used when importing cad-data that is scaled to millimeters or centimeters.
     * @param applyOutline Indicates whether the mesh gets an outline
     * @param outlineColor
     * @param cloneMaterials
     */
    load(meshName, addBarycentricCoordinates = false, pieceName, scale = 1, applyOutline, outlineColor, cloneMaterials) {
        if (this.meshName === meshName && this.pieceName === pieceName)
            return false;
        this.meshName = meshName;
        this.pieceName = pieceName;
        this.scale = scale;
        this.applyOutline = applyOutline;
        this.outlineColor = outlineColor;
        this.cloneMaterials = cloneMaterials;
        if (this.onMeshStartLoadingCallback !== undefined) {
            this.onMeshStartLoadingCallback(this);
        }
        const loadId = meshLoadingTracker.beginLoad(meshName, pieceName, this.shouldTrackMeshLoadForShadow());
        this.renderer
            .getConnection()
            .askByMessageType(new LoadMeshMessage(meshName, addBarycentricCoordinates, pieceName), MeshLoadedMessage)
            .then((msg) => {
            this.handleMeshLoadedMessage(msg);
            this.renderer.getSystem(MaterialQualitySystem).changeMeshMaterialsInSceneGraph();
        })
            .catch((messages) => {
            console.error(`Failed on load mesh ${meshName}`, messages);
        })
            .finally(() => {
            meshLoadingTracker.endLoad(loadId);
        });
        return true;
    }
    handleMeshLoadedMessage(msg) {
        var _a, _b, _c, _d;
        if (this.meshName !== msg.meshName || (msg.pieceName !== undefined && this.pieceName !== msg.pieceName))
            return;
        this.removeMesh(); // remove old mesh if necessary.
        if (msg.pieceName) {
            if (msg.animationRoot) {
                this.pieceAnimation = MeshPieceAnimation.create(msg.animationRoot, msg.mesh);
            }
            this.group = (_b = (_a = this.pieceAnimation) === null || _a === void 0 ? void 0 : _a.mesh) !== null && _b !== void 0 ? _b : msg.mesh.clone(false);
            this.group.position.setScalar(0);
            this.group.quaternion.identity();
            this.group.scale.setScalar(1);
        }
        else {
            let hasSkinnedMesh = false;
            msg.mesh.traverse((node) => {
                if (node instanceof SkinnedMesh) {
                    hasSkinnedMesh = true;
                }
            });
            this.group = hasSkinnedMesh ? cloneSkeleton(msg.mesh) : msg.mesh.clone();
        }
        this.group.visible = this.isVisible;
        const hasAnimations = this.group.animations.length > 0;
        if (hasAnimations && !this.pieceAnimation) {
            this.animationPlayback = new AnimationPlayback(this.group, this.group.animations);
        }
        if (msg.pieceName) {
            this.group.position.setScalar(0);
        }
        this.group.traverse((node) => {
            var _a;
            node.updateMatrix();
            node.matrixWorld.copy(node.matrix);
            node.matrixAutoUpdate = hasAnimations && !this.pieceAnimation;
            node.frustumCulled = (_a = this.frustumCulled) !== null && _a !== void 0 ? _a : true;
            node.receiveShadow = this.receiveShadow;
            node.castShadow = this.castShadow;
            if (node instanceof Mesh && this.cloneMaterials) {
                if (!Array.isArray(node.material) && node.material) {
                    node.material = node.material.clone();
                }
                if (Array.isArray(node.material)) {
                    const materialArray = [];
                    for (const material of node.material) {
                        materialArray.push(material.clone());
                    }
                    node.material = materialArray;
                }
            }
            // Add optional outline shader
            if (node instanceof Mesh && this.applyOutline) {
                let color = new Color(0xff8800);
                if (this.outlineColor !== "") {
                    color = new Color(this.outlineColor);
                }
                const edges = new EdgesGeometry(node.geometry);
                const line = new LineSegments(edges, new LineBasicMaterial({ color: color.getHex(), linewidth: 1 }));
                node.add(line);
            }
        });
        // Scale each node with the scale parameter
        // TODO: The 1e-16 hack shouldn't be necessary. Somehow the changes introduced with the new ui caused vertexScaleFactor to be a ridiculously small number.
        // Unfortunately we couldn't find out why.
        if (this.scale !== undefined && (this.scale > 1e-16 || this.scale === 0)) {
            this.group.scale.set(this.scale, this.scale, this.scale);
            this.group.updateMatrix();
        }
        if (this.subMeshVisibilities) {
            for (const it in this.subMeshVisibilities) {
                const subMesh = this.group.getObjectByName(it);
                subMesh && (subMesh.visible = this.subMeshVisibilities[it]);
            }
        }
        this.setVisible(this.isVisible);
        this.offsetGroup.add(this.group);
        if (this.pieceAnimation) {
            this.offsetGroup.add(this.pieceAnimation.context);
            this.pieceAnimation.update(0);
        }
        if (this.animationPlayback || this.pieceAnimation) {
            animationFrame$.onPreRender$.add(this.updateAnimation);
        }
        this.offsetGroup.name = `MeshInstance_${this.group.name}`;
        this.offsetGroup.updateMatrix();
        this.offsetGroup.updateMatrixWorld();
        this.onMeshLoadedCallback(this.offsetGroup, this);
        if (hasAnimations && this.shadowOwnerModelUID !== undefined) {
            const primitiveIndex = (_c = msg.pieceName) === null || _c === void 0 ? void 0 : _c.match(/_(\d+)\$$/);
            const sourceParent = msg.mesh.parent;
            const isMaterialPart = primitiveIndex !== undefined && primitiveIndex !== null &&
                sourceParent !== null && !(sourceParent instanceof Mesh) &&
                sourceParent.children.length > 1 && sourceParent.children.every((child) => child instanceof Mesh) &&
                sourceParent.children[Number(primitiveIndex[1])] === msg.mesh;
            if (isMaterialPart) {
                const parentModel = (_d = modelPool.getModel(this.shadowOwnerModelUID)) === null || _d === void 0 ? void 0 : _d.getParent();
                if (!parentModel)
                    throw new Error(`[mesh] Material part "${msg.pieceName}" has no parent model`);
                this.animationOwnerUID = parentModel.getUID();
                animationRegistry.registerGroupMember(this.animationOwnerUID, this);
            }
            else {
                this.animationOwnerUID = this.shadowOwnerModelUID;
                animationRegistry.register(this.animationOwnerUID, this);
            }
        }
    }
    get playback() {
        var _a, _b;
        return (_b = (_a = this.pieceAnimation) === null || _a === void 0 ? void 0 : _a.playback) !== null && _b !== void 0 ? _b : this.animationPlayback;
    }
    get animationClips() {
        var _a, _b;
        return (_b = (_a = this.playback) === null || _a === void 0 ? void 0 : _a.clips.map((clip) => clip.name)) !== null && _b !== void 0 ? _b : [];
    }
    get animationClipIds() {
        var _a, _b;
        return (_b = (_a = this.playback) === null || _a === void 0 ? void 0 : _a.clipIds) !== null && _b !== void 0 ? _b : [];
    }
    get animationClipInfo() {
        var _a, _b;
        return (_b = (_a = this.playback) === null || _a === void 0 ? void 0 : _a.clipInfo) !== null && _b !== void 0 ? _b : [];
    }
    applyAnimationState(state) {
        var _a;
        (_a = this.playback) === null || _a === void 0 ? void 0 : _a.applyState(state);
        this.updateAnimation(0);
    }
    get animationTime() {
        var _a, _b;
        return (_b = (_a = this.playback) === null || _a === void 0 ? void 0 : _a.time) !== null && _b !== void 0 ? _b : 0;
    }
    get isAnimationPlaying() {
        var _a, _b;
        return (_b = (_a = this.playback) === null || _a === void 0 ? void 0 : _a.isPlaying) !== null && _b !== void 0 ? _b : false;
    }
    get animationLoop() {
        var _a, _b;
        return (_b = (_a = this.playback) === null || _a === void 0 ? void 0 : _a.loop) !== null && _b !== void 0 ? _b : true;
    }
    playAnimation() {
        var _a;
        (_a = this.playback) === null || _a === void 0 ? void 0 : _a.play();
        this.updateAnimation(0);
    }
    pauseAnimation() {
        var _a;
        (_a = this.playback) === null || _a === void 0 ? void 0 : _a.pause();
    }
    resetAnimation() {
        var _a;
        (_a = this.playback) === null || _a === void 0 ? void 0 : _a.reset();
        this.updateAnimation(0);
    }
    setAnimationLoop(loop) {
        var _a;
        (_a = this.playback) === null || _a === void 0 ? void 0 : _a.setLoop(loop);
    }
    get selectedAnimationClip() {
        var _a, _b;
        return (_b = (_a = this.playback) === null || _a === void 0 ? void 0 : _a.selectedClip) !== null && _b !== void 0 ? _b : null;
    }
    selectAnimationClip(index, time = 0) {
        const playback = this.playback;
        if (!playback)
            throw new Error(`[mesh] Mesh "${this.meshName}" has no animation playback`);
        playback.selectClip(index, time);
        this.updateAnimation(0);
        if (this.animationOwnerUID !== undefined) {
            animationRegistry.onChanged.invoke(this.animationOwnerUID);
        }
    }
    /** check if a given sub mesh exists. Good for sanity-checks. */
    hasSubmesh(subMeshName) {
        if (this.group === undefined) {
            console.warn("Searching for a submesh within " + this.meshName + ", but not even the mesh itself was defined!");
            return false;
        }
        return this.group.getObjectByName(subMeshName) !== undefined;
    }
    setSubMeshTransform(subMeshName, position, orientation, scale) {
        const subMesh = this.group ? this.group.getObjectByName(subMeshName) : undefined;
        if (subMesh !== undefined) {
            subMesh.position.fromArray(position);
            subMesh.quaternion.fromArray(orientation !== null && orientation !== void 0 ? orientation : [0, 0, 0, 1]);
            subMesh.scale.fromArray(scale !== null && scale !== void 0 ? scale : [1, 1, 1]);
            subMesh.updateMatrix();
            subMesh.updateMatrixWorld(true);
            if (this.castShadow && this.isVisible) {
                this.triggerShadowUpdate();
            }
        }
    }
    setSubMeshVisibility(subMeshName, visible) {
        if (this.group) {
            const subMesh = this.group.getObjectByName(subMeshName);
            if (!subMesh)
                return;
            const changed = subMesh.visible !== visible;
            subMesh.visible = visible;
            if (this.castShadow && changed) {
                this.triggerShadowUpdate();
            }
        }
        else if (this.subMeshVisibilities) {
            this.subMeshVisibilities[subMeshName] = visible;
        }
    }
    updateSubMeshVisibilities(subMeshVisibilities) {
        if (this.group) {
            this.subMeshVisibilities = undefined;
            if (subMeshVisibilities) {
                for (const it in subMeshVisibilities) {
                    const subMesh = this.group.getObjectByName(it);
                    subMesh && (subMesh.visible = subMeshVisibilities[it]);
                }
            }
        }
        else {
            this.subMeshVisibilities = subMeshVisibilities;
        }
    }
    updateTransform(matrix) {
        this.offsetGroup.matrix.copy(matrix);
        this.offsetGroup.matrixWorldNeedsUpdate = true;
        if (!this.group)
            return;
        if (this.castShadow) {
            this.triggerShadowUpdate();
        }
    }
    shouldTrackMeshLoadForShadow() {
        if (this.shadowOwnerModelUID === undefined)
            return false;
        return this.renderer.isModelInCurrentRoom(this.shadowOwnerModelUID);
    }
    triggerShadowUpdate() {
        if (this.shadowOwnerModelUID !== undefined) {
            this.renderer.updateShadowForModel(this.shadowOwnerModelUID);
            return;
        }
        this.renderer.updateShadow();
    }
    traverseMesh(traverseCallback) {
        if (!this.group)
            return;
        this.group.traverse((child) => {
            if (child.isMesh) {
                traverseCallback(child);
            }
        });
    }
    traverseMeshByObjectName(name, traverseCallback) {
        if (!this.group)
            return;
        this.group.traverse((child) => {
            if (child.isMesh && child.name === name) {
                traverseCallback(child);
            }
        });
    }
    traverseMaterial(type, callback) {
        if (!this.group)
            return;
        this.group.traverse((child) => {
            if (child.isMesh) {
                const mesh = child;
                if (Array.isArray(mesh.material)) {
                    for (const mat of mesh.material) {
                        if (mat instanceof type) {
                            callback(mat, mesh);
                        }
                    }
                }
                else if (mesh.material instanceof type) {
                    callback(mesh.material, mesh);
                }
            }
        });
    }
    traverseMaterialByName(name, callback) {
        if (!this.group)
            return;
        this.group.traverse((child) => {
            if (child.isMesh) {
                const mesh = child;
                if (Array.isArray(mesh.material)) {
                    for (let i = 0, len = mesh.material.length; i < len; ++i) {
                        const mat = mesh.material[i];
                        if (mat.name === name) {
                            callback(mat, mesh, i);
                        }
                    }
                }
                else if (mesh.material.name === name) {
                    callback(mesh.material, mesh);
                }
            }
        });
    }
    findMaterialByName(name) {
        if (!this.group)
            return undefined;
        const objects = [];
        objects.push(this.group);
        objects.push(...this.group.children);
        for (const obj of objects) {
            if (obj.isMesh) {
                const mesh = obj;
                if (Array.isArray(mesh.material)) {
                    for (const mat of mesh.material) {
                        if (mat.name === name) {
                            return mat;
                        }
                    }
                }
                else if (mesh.material.name === name) {
                    return mesh.material;
                }
            }
            objects.push(...obj.children);
        }
        return undefined;
    }
    findSubObjectByName(name) {
        if (!this.group)
            return undefined;
        return this.group.getObjectByName(name);
    }
}
